import React, { useState, useMemo } from 'react';
import { 
  Calculator, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Play,
  Save, Trash2, GitCompare, BarChart3, Package, Users, Store, DollarSign,
  Clock, Percent, ArrowRight, RefreshCw, Layers, ChevronDown, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { simulatorAPI } from '../lib/api';
import { formatCurrency, formatPercent, cn } from '../lib/utils';
import { toast } from 'sonner';

// Enhanced decision types with more options
const decisionTypes = [
  // Marketing
  { value: 'marketing_budget', label: 'Marketing Budget Change', unit: '%', category: 'marketing', icon: BarChart3, description: 'Increase or decrease overall marketing spend' },
  { value: 'channel_budget', label: 'Channel-Specific Budget', unit: 'lakh', category: 'marketing', icon: Layers, description: 'Adjust budget for a specific marketing channel' },
  
  // Pricing & Revenue
  { value: 'pricing', label: 'Pricing Change', unit: '%', category: 'revenue', icon: DollarSign, description: 'Adjust product pricing across categories' },
  { value: 'discount_strategy', label: 'Discount Strategy', unit: '%', category: 'revenue', icon: Percent, description: 'Change discount depth or frequency' },
  
  // Operations
  { value: 'inventory_investment', label: 'Inventory Investment', unit: 'lakh', category: 'operations', icon: Package, description: 'Increase or decrease inventory levels' },
  { value: 'payment_terms', label: 'Payment Terms Change', unit: 'days', category: 'operations', icon: Clock, description: 'Negotiate supplier/customer payment terms' },
  
  // Growth
  { value: 'hiring', label: 'New Hires', unit: 'people', category: 'growth', icon: Users, description: 'Add new team members' },
  { value: 'expansion', label: 'Market Expansion', unit: 'lakh', category: 'growth', icon: Store, description: 'New warehouse or market entry capex' },
  { value: 'new_channel', label: 'New Channel Launch', unit: 'lakh', category: 'growth', icon: Store, description: 'Launch on a new sales channel' },
  { value: 'category_expansion', label: 'New Category Launch', unit: 'lakh', category: 'growth', icon: Package, description: 'Add new product category' },
];

const categoryLabels = {
  marketing: { label: 'Marketing', color: 'primary' },
  revenue: { label: 'Revenue & Pricing', color: 'emerald' },
  operations: { label: 'Operations', color: 'amber' },
  growth: { label: 'Growth & Expansion', color: 'blue' },
};

// Channel options for channel-specific decisions
const channels = [
  { value: 'meta', label: 'Meta (Facebook/Instagram)' },
  { value: 'google', label: 'Google Ads' },
  { value: 'amazon', label: 'Amazon Ads' },
  { value: 'flipkart', label: 'Flipkart Ads' },
  { value: 'influencer', label: 'Influencer Marketing' },
  { value: 'affiliate', label: 'Affiliate Marketing' },
];

// Sensitivity analysis ranges
const sensitivityRanges = {
  worst: 0.7,
  base: 1.0,
  best: 1.3,
};

export default function SimulatorPage() {
  const [selectedType, setSelectedType] = useState('marketing_budget');
  const [changeValue, setChangeValue] = useState(10);
  const [selectedChannel, setSelectedChannel] = useState('meta');
  const [description, setDescription] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [scenarioName, setScenarioName] = useState('');
  const [showSensitivity, setShowSensitivity] = useState(false);
  const [sensitivityResults, setSensitivityResults] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState('marketing');

  const currentDecision = decisionTypes.find((d) => d.value === selectedType);
  
  // Group decision types by category
  const groupedDecisions = useMemo(() => {
    const groups = {};
    decisionTypes.forEach(dt => {
      if (!groups[dt.category]) groups[dt.category] = [];
      groups[dt.category].push(dt);
    });
    return groups;
  }, []);

  // Get slider range based on decision type
  const getSliderRange = (type) => {
    switch(type) {
      case 'hiring':
        return { min: 1, max: 50, step: 1 };
      case 'expansion':
      case 'new_channel':
      case 'category_expansion':
      case 'inventory_investment':
      case 'channel_budget':
        return { min: 0, max: 200, step: 10 };
      case 'payment_terms':
        return { min: -30, max: 30, step: 5 };
      default:
        return { min: -50, max: 50, step: 5 };
    }
  };

  const sliderRange = getSliderRange(selectedType);

  const handleSimulate = async () => {
    setLoading(true);
    try {
      const response = await simulatorAPI.simulate({
        decision_type: selectedType,
        change_value: changeValue,
        channel: selectedType === 'channel_budget' ? selectedChannel : undefined,
        description: description || `${currentDecision?.label}: ${changeValue}${currentDecision?.unit}`,
      });
      setResult(response.data);
      toast.success('Simulation completed');
    } catch (err) {
      toast.error('Simulation failed');
      console.error('Simulation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSensitivityAnalysis = async () => {
    setLoading(true);
    try {
      const results = {};
      for (const [scenario, multiplier] of Object.entries(sensitivityRanges)) {
        const adjustedValue = Math.round(changeValue * multiplier);
        const response = await simulatorAPI.simulate({
          decision_type: selectedType,
          change_value: adjustedValue,
          description: `${currentDecision?.label}: ${adjustedValue}${currentDecision?.unit}`,
        });
        results[scenario] = { ...response.data, inputValue: adjustedValue };
      }
      setSensitivityResults(results);
      setShowSensitivity(true);
      toast.success('Sensitivity analysis completed');
    } catch (err) {
      toast.error('Sensitivity analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const saveScenario = () => {
    if (!result || !scenarioName) return;
    
    const scenario = {
      id: Date.now(),
      name: scenarioName,
      type: selectedType,
      value: changeValue,
      result: result,
      savedAt: new Date().toISOString(),
    };
    
    setSavedScenarios(prev => [scenario, ...prev]);
    setShowSaveDialog(false);
    setScenarioName('');
    toast.success('Scenario saved');
  };

  const deleteScenario = (id) => {
    setSavedScenarios(prev => prev.filter(s => s.id !== id));
    toast.success('Scenario deleted');
  };

  const getImpactColor = (value) => {
    if (value > 5) return 'text-emerald-600';
    if (value > 0) return 'text-emerald-500';
    if (value > -5) return 'text-amber-600';
    return 'text-red-600';
  };

  const getImpactBg = (value) => {
    if (value > 5) return 'bg-emerald-50 border-emerald-200';
    if (value > 0) return 'bg-emerald-50/50 border-emerald-100';
    if (value > -5) return 'bg-amber-50 border-amber-200';
    return 'bg-red-50 border-red-200';
  };

  return (
    <div className="space-y-6" data-testid="simulator-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Decision Impact Simulator</h2>
          <p className="text-slate-500">Model the financial impact of strategic decisions before execution</p>
        </div>
        <div className="flex items-center gap-2">
          {savedScenarios.length >= 2 && (
            <Button variant="outline" onClick={() => setShowCompare(true)} data-testid="compare-btn">
              <GitCompare className="w-4 h-4 mr-2" /> Compare ({savedScenarios.length})
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Input Panel */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-5">
              <Calculator className="w-5 h-5 text-primary-600" />
              <h3 className="font-heading font-semibold text-lg text-slate-900">Scenario Input</h3>
            </div>

            <div className="space-y-5">
              {/* Decision Type - Categorized */}
              <div className="space-y-2">
                <Label>Decision Type</Label>
                <div className="space-y-2">
                  {Object.entries(groupedDecisions).map(([category, decisions]) => (
                    <div key={category} className="border border-slate-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
                        className={cn(
                          'w-full flex items-center justify-between p-2.5 text-sm font-medium transition-colors',
                          categoryLabels[category].color === 'primary' && 'bg-primary-50 text-primary-700 hover:bg-primary-100',
                          categoryLabels[category].color === 'emerald' && 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
                          categoryLabels[category].color === 'amber' && 'bg-amber-50 text-amber-700 hover:bg-amber-100',
                          categoryLabels[category].color === 'blue' && 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                        )}
                      >
                        <span>{categoryLabels[category].label}</span>
                        {expandedCategory === category ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </button>
                      {expandedCategory === category && (
                        <div className="p-2 space-y-1 bg-white">
                          {decisions.map((dt) => (
                            <button
                              key={dt.value}
                              onClick={() => setSelectedType(dt.value)}
                              className={cn(
                                'w-full flex items-center gap-2 p-2 rounded-lg text-left text-sm transition-colors',
                                selectedType === dt.value
                                  ? 'bg-primary-100 text-primary-700 font-medium'
                                  : 'hover:bg-slate-50 text-slate-600'
                              )}
                            >
                              <dt.icon className="w-4 h-4" />
                              <span>{dt.label}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-1">{currentDecision?.description}</p>
              </div>

              {/* Channel selector for channel-specific decisions */}
              {selectedType === 'channel_budget' && (
                <div className="space-y-2">
                  <Label>Select Channel</Label>
                  <Select value={selectedChannel} onValueChange={setSelectedChannel}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {channels.map((ch) => (
                        <SelectItem key={ch.value} value={ch.value}>
                          {ch.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Change Value */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label>Change Amount</Label>
                  <span className="font-mono text-lg font-bold text-primary-700">
                    {changeValue > 0 && '+'}{changeValue}{currentDecision?.unit}
                  </span>
                </div>
                <Slider
                  value={[changeValue]}
                  onValueChange={(v) => setChangeValue(v[0])}
                  min={sliderRange.min}
                  max={sliderRange.max}
                  step={sliderRange.step}
                  className="w-full"
                  data-testid="change-value-slider"
                />
                <div className="flex justify-between text-xs text-slate-500">
                  <span>{sliderRange.min}{currentDecision?.unit}</span>
                  <span>{sliderRange.max}{currentDecision?.unit}</span>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label>Description (optional)</Label>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g., Increase Meta budget for festive season"
                  data-testid="description-input"
                />
              </div>

              {/* Action Buttons */}
              <div className="space-y-2">
                <Button
                  onClick={handleSimulate}
                  disabled={loading}
                  className="w-full bg-primary-700 hover:bg-primary-800"
                  data-testid="simulate-button"
                >
                  <Play className="w-4 h-4 mr-2" />
                  {loading ? 'Simulating...' : 'Run Simulation'}
                </Button>
                
                <Button
                  onClick={handleSensitivityAnalysis}
                  disabled={loading}
                  variant="outline"
                  className="w-full"
                  data-testid="sensitivity-button"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Sensitivity Analysis
                </Button>
              </div>
            </div>
          </div>

          {/* Quick Scenarios */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <p className="text-sm font-medium text-slate-700 mb-3">Quick Scenarios</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { type: 'marketing_budget', value: 20, label: '+20% Marketing', icon: BarChart3 },
                { type: 'pricing', value: -5, label: '-5% Price Cut', icon: DollarSign },
                { type: 'hiring', value: 10, label: 'Hire 10', icon: Users },
                { type: 'inventory_investment', value: 50, label: '₹50L Inventory', icon: Package },
                { type: 'new_channel', value: 30, label: 'New Channel', icon: Store },
                { type: 'payment_terms', value: 15, label: '+15d Supplier Terms', icon: Clock },
              ].map((scenario, index) => {
                const Icon = scenario.icon;
                return (
                  <button
                    key={index}
                    onClick={() => {
                      setSelectedType(scenario.type);
                      setChangeValue(scenario.value);
                    }}
                    className="flex items-center gap-2 p-2.5 bg-white rounded-lg border border-slate-200 text-sm hover:border-primary-300 hover:bg-primary-50 transition-colors"
                  >
                    <Icon className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-700">{scenario.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Results Panel */}
        <div className="lg:col-span-2 space-y-6">
          {result ? (
            <>
              {/* Impact Summary */}
              <div className={cn('border rounded-xl p-6', getImpactBg(result.impact_ebitda_pct))}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-heading font-semibold text-lg text-slate-900">Impact Summary</h3>
                    <p className="text-sm text-slate-500">{result.input?.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setScenarioName(result.input?.description || '');
                        setShowSaveDialog(true);
                      }}
                    >
                      <Save className="w-4 h-4 mr-1" /> Save
                    </Button>
                    {result.impact_ebitda_pct > 0 ? (
                      <CheckCircle className="w-8 h-8 text-emerald-500" />
                    ) : (
                      <AlertTriangle className="w-8 h-8 text-amber-500" />
                    )}
                  </div>
                </div>

                {/* Before/After Comparison */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-white/50 rounded-lg p-4 border border-slate-200">
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Before</p>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Revenue</span>
                        <span className="font-mono text-sm">{formatCurrency(result.baseline?.revenue || 100000000, { compact: true })}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">EBITDA</span>
                        <span className="font-mono text-sm">{formatCurrency(result.baseline?.ebitda || 8000000, { compact: true })}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Runway</span>
                        <span className="font-mono text-sm">{result.baseline?.runway || 18} months</span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border-2 border-primary-300">
                    <p className="text-xs text-primary-600 uppercase tracking-wide mb-2">After</p>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">Revenue</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-medium">{formatCurrency((result.baseline?.revenue || 100000000) + (result.impact_pl?.revenue_change || 0), { compact: true })}</span>
                          <span className={cn('text-xs', getImpactColor(result.impact_pl?.revenue_change_pct))}>
                            ({formatPercent(result.impact_pl?.revenue_change_pct, { showSign: true })})
                          </span>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">EBITDA</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-medium">{formatCurrency((result.baseline?.ebitda || 8000000) + (result.impact_pl?.ebitda_change || 0), { compact: true })}</span>
                          <span className={cn('text-xs', getImpactColor(result.impact_ebitda_pct))}>
                            ({formatPercent(result.impact_ebitda_pct, { showSign: true })})
                          </span>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">Runway</span>
                        <span className="font-mono text-sm font-medium">{result.impact_runway} months</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="text-center">
                    <p className="text-sm text-slate-500">Revenue Impact</p>
                    <p className={cn('font-mono text-xl font-bold', getImpactColor(result.impact_pl?.revenue_change_pct))}>
                      {formatPercent(result.impact_pl?.revenue_change_pct, { showSign: true })}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-slate-500">EBITDA Impact</p>
                    <p className={cn('font-mono text-xl font-bold', getImpactColor(result.impact_ebitda_pct))}>
                      {formatPercent(result.impact_ebitda_pct, { showSign: true })}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-slate-500">Cash Impact</p>
                    <p className={cn('font-mono text-xl font-bold', getImpactColor(-result.impact_cashflow?.immediate_cash_impact))}>
                      {formatCurrency(result.impact_cashflow?.immediate_cash_impact, { compact: true })}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-slate-500">New Runway</p>
                    <p className="font-mono text-xl font-bold text-slate-900">
                      {result.impact_runway} mo
                    </p>
                  </div>
                </div>

                <div className="p-4 bg-white/70 rounded-lg border border-slate-200">
                  <p className="font-medium text-slate-900 mb-1">Recommendation</p>
                  <p className="text-slate-600">{result.recommendation}</p>
                </div>
              </div>

              {/* Detailed Impact */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* P&L Impact */}
                <div className="bg-white border border-slate-200 rounded-xl p-6">
                  <h4 className="font-heading font-semibold text-slate-900 mb-4">P&L Impact</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-slate-100">
                      <span className="text-slate-600">Revenue Change</span>
                      <div className="text-right">
                        <span className={cn('font-mono font-medium', getImpactColor(result.impact_pl?.revenue_change))}>
                          {formatCurrency(result.impact_pl?.revenue_change, { compact: true })}
                        </span>
                        <span className="text-xs text-slate-500 ml-2">
                          ({formatPercent(result.impact_pl?.revenue_change_pct, { showSign: true })})
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-slate-100">
                      <span className="text-slate-600">Cost Change</span>
                      <div className="text-right">
                        <span className={cn('font-mono font-medium', getImpactColor(-result.impact_pl?.cost_change))}>
                          {formatCurrency(result.impact_pl?.cost_change || 0, { compact: true })}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-slate-600 font-medium">EBITDA Change</span>
                      <div className="text-right">
                        <span className={cn('font-mono font-bold', getImpactColor(result.impact_pl?.ebitda_change))}>
                          {formatCurrency(result.impact_pl?.ebitda_change, { compact: true })}
                        </span>
                        <span className="text-xs text-slate-500 ml-2">
                          ({formatPercent(result.impact_pl?.ebitda_change_pct, { showSign: true })})
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Cash Flow Impact */}
                <div className="bg-white border border-slate-200 rounded-xl p-6">
                  <h4 className="font-heading font-semibold text-slate-900 mb-4">Cash Flow Impact</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-slate-100">
                      <span className="text-slate-600">Immediate Impact</span>
                      <span className={cn('font-mono font-medium', getImpactColor(-result.impact_cashflow?.immediate_cash_impact))}>
                        {formatCurrency(result.impact_cashflow?.immediate_cash_impact, { compact: true })}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-slate-100">
                      <span className="text-slate-600">Annual Impact</span>
                      <span className={cn('font-mono font-medium', getImpactColor(-result.impact_cashflow?.annual_cash_impact))}>
                        {formatCurrency(result.impact_cashflow?.annual_cash_impact, { compact: true })}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-slate-600 font-medium">Updated Runway</span>
                      <span className="font-mono font-bold text-slate-900">
                        {result.impact_runway} months
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-12 text-center">
              <Calculator className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">
                No Simulation Yet
              </h3>
              <p className="text-slate-500 max-w-md mx-auto">
                Select a decision type and adjust the parameters, then click "Run Simulation" to see the projected impact on your financials.
              </p>
            </div>
          )}

          {/* Saved Scenarios */}
          {savedScenarios.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h4 className="font-heading font-semibold text-slate-900 mb-4">Saved Scenarios</h4>
              <div className="space-y-2">
                {savedScenarios.map((scenario) => (
                  <div
                    key={scenario.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-sm text-slate-900">{scenario.name}</p>
                      <p className="text-xs text-slate-500">
                        {new Date(scenario.savedAt).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={cn('font-mono text-sm', getImpactColor(scenario.result.impact_ebitda_pct))}>
                        {formatPercent(scenario.result.impact_ebitda_pct, { showSign: true })} EBITDA
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteScenario(scenario.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Save Scenario Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Scenario</DialogTitle>
            <DialogDescription>
              Save this simulation to compare with other scenarios later.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="scenario-name">Scenario Name</Label>
            <Input
              id="scenario-name"
              value={scenarioName}
              onChange={(e) => setScenarioName(e.target.value)}
              placeholder="e.g., Aggressive Marketing Q4"
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>Cancel</Button>
            <Button onClick={saveScenario} disabled={!scenarioName}>
              <Save className="w-4 h-4 mr-2" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compare Scenarios Dialog */}
      <Dialog open={showCompare} onOpenChange={setShowCompare}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Compare Scenarios</DialogTitle>
            <DialogDescription>
              Side-by-side comparison of saved scenarios
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="px-3 py-2 text-left text-sm font-semibold">Scenario</th>
                    <th className="px-3 py-2 text-right text-sm font-semibold">Revenue Δ</th>
                    <th className="px-3 py-2 text-right text-sm font-semibold">EBITDA Δ</th>
                    <th className="px-3 py-2 text-right text-sm font-semibold">Cash Impact</th>
                    <th className="px-3 py-2 text-right text-sm font-semibold">Runway</th>
                  </tr>
                </thead>
                <tbody>
                  {savedScenarios.map((scenario) => (
                    <tr key={scenario.id} className="border-b">
                      <td className="px-3 py-3">
                        <div>
                          <p className="font-medium text-sm">{scenario.name}</p>
                          <p className="text-xs text-slate-500">{scenario.result.input?.description}</p>
                        </div>
                      </td>
                      <td className={cn('px-3 py-3 text-right font-mono text-sm', getImpactColor(scenario.result.impact_pl?.revenue_change_pct))}>
                        {formatPercent(scenario.result.impact_pl?.revenue_change_pct, { showSign: true })}
                      </td>
                      <td className={cn('px-3 py-3 text-right font-mono text-sm', getImpactColor(scenario.result.impact_ebitda_pct))}>
                        {formatPercent(scenario.result.impact_ebitda_pct, { showSign: true })}
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-sm">
                        {formatCurrency(scenario.result.impact_cashflow?.immediate_cash_impact, { compact: true })}
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-sm font-medium">
                        {scenario.result.impact_runway} mo
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompare(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sensitivity Analysis Dialog */}
      <Dialog open={showSensitivity} onOpenChange={setShowSensitivity}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Sensitivity Analysis</DialogTitle>
            <DialogDescription>
              Impact under different scenarios (Worst / Base / Best case)
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {sensitivityResults && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  {['worst', 'base', 'best'].map((scenario) => {
                    const data = sensitivityResults[scenario];
                    const isPositive = data.impact_ebitda_pct > 0;
                    return (
                      <div
                        key={scenario}
                        className={cn(
                          'p-4 rounded-lg border',
                          scenario === 'worst' && 'bg-red-50 border-red-200',
                          scenario === 'base' && 'bg-slate-50 border-slate-200',
                          scenario === 'best' && 'bg-emerald-50 border-emerald-200'
                        )}
                      >
                        <p className={cn(
                          'text-xs font-semibold uppercase tracking-wide mb-2',
                          scenario === 'worst' && 'text-red-600',
                          scenario === 'base' && 'text-slate-600',
                          scenario === 'best' && 'text-emerald-600'
                        )}>
                          {scenario === 'worst' ? '⚠️ Worst Case' : scenario === 'base' ? '📊 Base Case' : '✨ Best Case'}
                        </p>
                        <p className="text-sm text-slate-600 mb-3">
                          Input: {data.inputValue > 0 ? '+' : ''}{data.inputValue}{currentDecision?.unit}
                        </p>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">EBITDA Δ</span>
                            <span className={cn('font-mono font-medium', getImpactColor(data.impact_ebitda_pct))}>
                              {formatPercent(data.impact_ebitda_pct, { showSign: true })}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Revenue Δ</span>
                            <span className={cn('font-mono font-medium', getImpactColor(data.impact_pl?.revenue_change_pct))}>
                              {formatPercent(data.impact_pl?.revenue_change_pct, { showSign: true })}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Runway</span>
                            <span className="font-mono font-medium">{data.impact_runway} mo</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                    Sensitivity range: {Math.round(changeValue * sensitivityRanges.worst)} to {Math.round(changeValue * sensitivityRanges.best)} {currentDecision?.unit}
                  </p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSensitivity(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
